import { useMutation, useQuery } from "@apollo/client";
import { CREATE_EMPLOYMENT_MUTATION } from "../../person/mutations";
import EmploymentFormModal, { EmploymentFormData } from "./EmploymentFormModal";
import { GET_JOB_TITLES_QUERY, EmploymentRow } from "../../person/queries";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import { useParams } from "@/router";
import { PollForBandChangesFunction } from "@/hooks/usePollBandChanges";
import { Band } from "@/types";

interface CreateEmploymentModalProps {
  personId?: string;
  onClose(): void;
  employments?: EmploymentRow[];
  pollForBandChanges: PollForBandChangesFunction;
  fetchCurrentBands: (pgaId: string) => Promise<Band[]>;
}

export default function CreateEmploymentModal({
  personId,
  onClose,
  employments = [],
  pollForBandChanges,
  fetchCurrentBands
}: CreateEmploymentModalProps) {
  const { pgaId } = useParams("/people/:pgaId/career/employment");
  const { data: jobTitlesData } = useQuery(GET_JOB_TITLES_QUERY, {
    variables: { personId: personId! },
    skip: !personId
  });

  const availableJobTitles = jobTitlesData?.person.availableJobTitles || [];
  const tourCodes = jobTitlesData?.tourCodes || [];

  const [createEmploymentMutation] = useMutation(CREATE_EMPLOYMENT_MUTATION, {
    refetchQueries: [
      {
        query: PERSON_DETAILS_HEADER_QUERY,
        variables: { pgaId }
      }
    ],
    awaitRefetchQueries: true
  });

  const onSubmit = async ({ workingLocation, note, ...formData }: EmploymentFormData) => {
    // Capture baseline BEFORE mutation to ensure we detect new bands
    let baselineBands: Band[] | undefined;

    try {
      baselineBands = await fetchCurrentBands(pgaId);
    } catch (e) {
      console.warn("[CreateEmployment] Failed to capture baseline, will capture during polling:", e);
    }

    const input = {
      personId,
      ...formData,
      workingLocation: workingLocation === "ON_SITE" ? "on_site" : "remote",
      note: note ? { content: note, priority: "NONE", title: "Add Employment" } : undefined
    };
    const { data } = await createEmploymentMutation({
      variables: {
        input
      }
    });

    const result = data?.createEmployment || { success: false, message: "Failed to create employment" };

    // If mutation succeeded, poll for band changes
    // Use pgaId from route params since the query requires pgaId, not person.id
    if (result.success) {
      // Try to get facility section from the newly created employment in the response
      const newEmployment = data?.createEmployment?.employment?.person?.employments?.find(
        (emp: EmploymentRow) => emp.facility?.id === formData.facilityId && emp.startedOn === formData.startedOn
      );
      const facilitySectionLegacyId = newEmployment?.facility?.section?.legacyId;

      const expectedChanges = {
        classificationName: formData.jobClassification,
        startedOn: formData.startedOn,
        sectionLegacyId: facilitySectionLegacyId
      };

      // Poll for band changes in the background, passing baseline if we captured it
      pollForBandChanges(pgaId, expectedChanges, baselineBands).catch((error) => {
        console.error("[CreateEmployment] Error polling for band changes:", error);
      });
    }

    return result;
  };

  return (
    <EmploymentFormModal
      open
      headline="Create Employment"
      availableJobTitles={availableJobTitles}
      tourCodes={tourCodes}
      onSubmit={onSubmit}
      onClose={onClose}
      employments={employments}
    />
  );
}
